import React, { FC, useState } from 'react';
import { Button, CircularProgress, Dialog, DialogContent, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface DeleteInviteDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  inviteEmail: string;
}

export const DeleteInviteDialog: FC<DeleteInviteDialogProps> = ({
  open,
  onClose,
  onConfirm,
  inviteEmail,
}) => {
  const { t } = useTranslation();
  const [isDeleting, setIsDeleting] = useState(false);

  const handleConfirm = async () => {
    setIsDeleting(true);
    try {
      await onConfirm();
      onClose();
    } catch (error) {
      // Error handling is done in the parent component
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Dialog open={open} maxWidth="sm" fullWidth>
      <DialogContent>
        <Typography variant="MHHeading" mb={1}>
          Delete Invite
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          Are you sure you want to delete the invite for <strong>{inviteEmail}</strong>? This action
          cannot be undone.
        </Typography>
        <Grid container direction="row" justifyContent="flex-end">
          <Grid item>
            <Button
              variant="brandContained"
              color="brandGrey"
              onClick={onClose}
              disabled={isDeleting}
              sx={{ mr: 1 }}
              data-cy="cancel-delete-invite-button">
              {t('actions.cancel')}
            </Button>
            <Button
              onClick={handleConfirm}
              variant="brandContained"
              color="brandBlack"
              startIcon={isDeleting ? <CircularProgress size="1em" /> : undefined}
              disabled={isDeleting}
              data-cy="confirm-delete-invite-button"
              sx={{
                backgroundColor: 'error.main',
                '&:hover': {
                  backgroundColor: 'error.dark',
                },
              }}>
              Delete
            </Button>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};
